import { useState } from 'react'

import { useHealthSignal, useWindow } from '@krp/client-runtime'
import {
  OWN_RECHECK_ACTION,
  OwnActionsDialog,
  OwnCashierPanel,
  ownRegisterHealth,
  shiftMarkOf,
  shiftProblems,
  shiftText,
  tillWhere,
  useAgentSession,
  useOwnCashier,
  useOwnCatalog,
  useOwnSale,
  useOwnSnapshot,
  useOwnTill,
} from '@krp/resource-own-ui'
import { SaleScreen, type CheckoutTill } from '@krp/sale-ui'

import { MaintenanceGate } from '../../app/MaintenanceGate'
import { AGENT_MIN_API_LEVEL } from '../platform/hardwareLinks'
import { OwnDeliveryStrip } from './OwnDeliveryStrip'
import { useOwnStore } from './useOwnStore'

/**
 * Окно «РМК» источника «Собственная» (RP-SALE-UI, S4): общий экран продажи, а под ним — оборудование ЭТОГО компьютера через кассовый
 * агент. rid — из адреса окна. Привязка тонкая (RP-SEP3): состояние и правила продажи, кассира, снимка, сессии агента и команд кассе
 * живут в `resource-own-ui`; здесь только store приложения и сборка колонки расчёта. Кассир — не вошедший человек, а выбранный из
 * списка и подтверждённый PIN (RP-OWN11): его GUID уходит в чек, смену, наличные и X-отчёт. Связь кассового агента и кассы — сигналами
 * в точку «Связь» шапки (RP-UI-LINK, шаг 5); «Проверить заново» из мини-окна — полный поиск агента и чтение кассы.
 */
export function OwnSaleBinding() {
  const window = useWindow()
  const resourceId = window.params.rid ?? ''
  const store = useOwnStore()
  const agent = useAgentSession({ dispatch: store.dispatch, minApiLevel: AGENT_MIN_API_LEVEL })
  const cashierSession = useOwnCashier(resourceId, store)
  const snapshot = useOwnSnapshot(resourceId, store)
  const { cashier } = cashierSession
  const sale = useOwnSale({ resourceId, store, agent, useCatalog: useOwnCatalog, cashier, snapshot })
  const till = useOwnTill(agent, cashierSession)

  const [actionsOpen, setActionsOpen] = useState(false)
  const { model } = agent
  const health = ownRegisterHealth(model)
  const onHealthAction = (key: string) => {
    if (key === OWN_RECHECK_ACTION) till.recheck()
  }
  useHealthSignal(health.agent, onHealthAction)
  useHealthSignal(health.register, onHealthAction)

  const startedCashier = snapshot.current?.draft.cashier ?? null
  const retryCashierName = startedCashier !== null && startedCashier.cashierId !== undefined && startedCashier.cashierId !== (cashier?.cashierId ?? '') ? startedCashier.name : null
  // Начатая операция допускает повтор и без свежего подтверждения: тело и кассир берутся из снимка (то же списание не повторится).
  const retryOnly = cashier === null && snapshot.current !== null
  const cashierPayReason = retryOnly ? null : cashierSession.blockReason
  const checkoutTill: CheckoutTill = {
    shift: shiftMarkOf(model),
    where: tillWhere(model),
    payBlockedReason: model.gate.open ? cashierPayReason : model.gate.text,
    onActions: () => setActionsOpen(true),
  }

  if (resourceId === '') return <p role="alert">Не указан источник продажи.</p>

  // RP-MNT: во время обновления РМК закрыта замком; открытый чек разрешаем довести (слив), новую продажу — нет.
  return (
    <MaintenanceGate resourceId={resourceId} label={`Собственная · ${tillWhere(model)}`}>
      <OwnCashierPanel session={cashierSession} retryCashierName={retryCashierName} />
      <SaleScreen vm={sale.vm} actions={sale.actions} till={checkoutTill} />
      <OwnDeliveryStrip agent={agent} resourceId={resourceId} dispatch={store.dispatch} />
      {actionsOpen && (
        <OwnActionsDialog
          where={tillWhere(model)}
          shiftMark={shiftMarkOf(model)}
          shiftText={shiftText(model)}
          shiftProblems={shiftProblems(model)}
          busy={model.busy !== null}
          locked={model.pendingUnknown !== null}
          cashierBlockedReason={cashierSession.blockReason}
          priceTypes={sale.priceTypes}
          priceTypeKey={sale.priceTypeKey}
          priceTypeLockedReason={sale.priceTypeLockedReason}
          totals={till.totals}
          result={till.result}
          onReadTotals={till.readTotals}
          onOpenShift={till.openShift}
          onCloseShift={till.closeShift}
          onCash={till.cash}
          onPrintReportX={till.printReportX}
          onPrintReceiptCopy={till.printReceiptCopy}
          onChoosePriceType={sale.choosePriceType}
          onClose={() => {
            setActionsOpen(false)
            till.clearResult()
          }}
        />
      )}
    </MaintenanceGate>
  )
}
